import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageShell, type PageShellRender } from '@/components/PageShell';
import { KpiStrip, type Kpi } from '@/components/KpiStrip';
import { Panel, SegmentedControl, EmptyState } from '@/components/Panel';
import { MetricTable, TrendCell, type MetricColumn } from '@/components/MetricTable';
import { LineChart } from '@/components/charts/LineChart';
import { DataSourceBanner } from '@/features/availability/DataSourceBanner';
import {
  fetchDiscount, type Cell, type DiscountHotspot, type DiscountOverview,
  type DiscountRow, type DiscountSpreadRow,
} from '@/lib/pages.api';
import { deltaPct, deltaPoints, inr, inrExact, pctOf, plain, shortDate, tone, units } from '@/lib/money';
import { titleCase } from '@/lib/format';

/**
 * Discount — price realisation, and where the margin is going.
 *
 * No stylesheet of its own: the page is Panels, a KpiStrip and MetricTables,
 * and a module that only re-declares the shell's own layout is a second place
 * for it to drift.
 *
 * DISCOUNT IS WEIGHTED BY MONEY, NOT BY SKU. Every figure is
 * 1 − selling-price sales ÷ MRP sales over summed atoms, so a ₹500 pack counts
 * for ten times a ₹50 sachet. The mean of per-SKU discount percentages, which
 * is what most price reports show, is a different number that happens to look
 * plausible.
 *
 * THE PAGE IS BUILT AROUND SKU × CITY. A national discount figure for a SKU is
 * an average of an 8% city and a 34% city and is true of neither, so the two
 * panels that drive action — the deepest cells, and the widest same-SKU gap
 * between cities — are both at that grain.
 */

const GRAIN_LABEL = { city: 'City', category: 'Category', sku: 'SKU' } as const;
type Grain = keyof typeof GRAIN_LABEL;

const sub = (c: Cell, invert = false): { text: string; tone?: 'up' | 'down' } | undefined => {
  const d = deltaPct(c.delta_pct);
  if (!d) return undefined;
  const t = tone(c.delta_pct, invert);
  return t ? { text: d, tone: t } : { text: d };
};

export function DiscountPage({
  onNavigate,
  onSignOut,
}: {
  onNavigate?: (section: string, page: string) => void;
  onSignOut?: () => void;
}) {
  const [resolved, setResolved] = useState<DiscountOverview['window'] | undefined>(undefined);
  return (
    <PageShell
      title="Discount"
      activeNavId="discount"
      activeSection="discount"
      {...(onNavigate ? { onNavigate } : {})}
      {...(onSignOut ? { onSignOut } : {})}
      resolved={resolved}
    >
      {(ctx) => <DiscountBody ctx={ctx} onResolved={setResolved} />}
    </PageShell>
  );
}

function DiscountBody({
  ctx,
  onResolved,
}: {
  ctx: PageShellRender;
  onResolved: (w: DiscountOverview['window']) => void;
}) {
  const [grain, setGrain] = useState<Grain>('sku');
  const [chartMetric, setChartMetric] = useState<'discount' | 'prices'>('discount');

  const params = useMemo(
    () => ({
      customer_name: ctx.customer,
      window: ctx.range,
      filters: ctx.scope,
      limit: 12,
    }),
    [ctx.customer, ctx.range, ctx.scope],
  );

  const query = useQuery({
    queryKey: ['discount-overview', params],
    queryFn: ({ signal }) => fetchDiscount(params, signal),
    enabled: ctx.customer !== '',
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });

  const data = query.data;
  const win = data?.window;
  useEffect(() => {
    if (win) onResolved(win);
  }, [win, onResolved]);
  const loading = query.isPending && !data;

  const kpis: Kpi[] = useMemo(() => {
    const k = data?.kpis;
    return [
      {
        key: 'discount',
        label: 'Discount',
        value: pctOf(k?.discount_pct.value ?? null),
        delta: deltaPoints(k?.discount_pct.delta),
        // Discounting deeper is not an improvement, whatever it does to volume.
        tone: tone(k?.discount_pct.delta, true),
        help: data?.response_meta.basis_note,
        emphasis: true,
      },
      {
        key: 'given',
        label: 'Given away',
        value: inr(k?.discount_value.value ?? null),
        delta: deltaPct(k?.discount_value.delta_pct),
        tone: tone(k?.discount_value.delta_pct, true),
        foot: 'MRP sales less selling-price sales',
        emphasis: true,
      },
      {
        key: 'asp',
        label: 'Avg selling price',
        value: inrExact(k?.asp.value ?? null),
        delta: deltaPct(k?.asp.delta_pct),
        tone: tone(k?.asp.delta_pct),
      },
      {
        key: 'mrp',
        label: 'Avg MRP',
        value: inrExact(k?.avg_mrp.value ?? null),
        delta: deltaPct(k?.avg_mrp.delta_pct),
        tone: tone(k?.avg_mrp.delta_pct),
      },
      {
        key: 'sales',
        label: 'Sales',
        value: inr(k?.sales.value ?? null),
        delta: deltaPct(k?.sales.delta_pct),
        tone: tone(k?.sales.delta_pct),
      },
      {
        key: 'units',
        label: 'Units',
        value: units(k?.units.value ?? null),
        delta: deltaPct(k?.units.delta_pct),
        tone: tone(k?.units.delta_pct),
      },
      {
        key: 'deep',
        label: `Cells over ${data?.response_meta.deep_discount_pct ?? 25}%`,
        value: plain(k?.deep_discount_cells.value ?? null),
        foot: 'among the deepest shown',
        help: 'Counted over the deepest-discount panel, which is capped — so this is "among the cells listed", not a census.',
      },
      {
        key: 'spread',
        label: 'Widest city gap',
        value: k?.widest_spread_pct.value != null ? `${k.widest_spread_pct.value.toFixed(1)} pts` : '—',
        foot: 'same SKU, two cities',
      },
    ];
  }, [data]);

  const chart = useMemo(() => {
    const s = data?.series ?? [];
    const labels = s.map((p) => shortDate(p.day));
    if (chartMetric === 'prices') {
      return {
        labels,
        series: [
          {
            key: 'mrp',
            label: 'Avg MRP',
            color: '#b7b7b7',
            points: s.map((p) => p.avg_mrp),
            format: (v: number) => inrExact(v),
          },
          {
            key: 'asp',
            label: 'Avg selling price',
            color: '#342885',
            points: s.map((p) => p.asp),
            format: (v: number) => inrExact(v),
          },
        ],
      };
    }
    return {
      labels,
      series: [
        {
          key: 'discount',
          label: 'Discount',
          color: '#d03941',
          points: s.map((p) => p.discount_pct),
          format: (v: number) => pctOf(v),
        },
      ],
    };
  }, [data, chartMetric]);

  const columns: MetricColumn<DiscountRow>[] = useMemo(
    () => [
      {
        key: 'label',
        header: GRAIN_LABEL[grain],
        align: 'left',
        width: '26%',
        render: (r) => (grain === 'city' ? (titleCase(r.label) ?? r.label) : r.label),
      },
      {
        key: 'discount',
        header: 'Discount',
        render: (r) => pctOf(r.discount_pct.value),
        sub: (r) => {
          const d = deltaPoints(r.discount_pct.delta);
          if (!d) return undefined;
          const t = tone(r.discount_pct.delta, true);
          return t ? { text: d, tone: t } : { text: d };
        },
      },
      { key: 'asp', header: 'Selling price', render: (r) => inrExact(r.asp.value), sub: (r) => sub(r.asp) },
      { key: 'mrp', header: 'MRP', render: (r) => inrExact(r.avg_mrp.value) },
      {
        key: 'given',
        header: 'Given away',
        render: (r) => inr(r.discount_value.value),
        sub: (r) => sub(r.discount_value, true),
      },
      {
        key: 'share',
        header: 'Share of giveaway',
        render: (r) => pctOf(r.share_of_discount_pct),
      },
      { key: 'units', header: 'Units', render: (r) => units(r.units.value), sub: (r) => sub(r.units) },
      { key: 'sales', header: 'Sales', render: (r) => inr(r.sales.value), sub: (r) => sub(r.sales) },
      {
        key: 'trend',
        header: 'Sales trend',
        align: 'center',
        render: (r) => <TrendCell points={r.series ?? null} />,
      },
    ],
    [grain],
  );

  const hotspotColumns: MetricColumn<DiscountHotspot>[] = [
    {
      key: 'sku',
      header: 'SKU',
      align: 'left',
      width: '30%',
      render: (h) => h.sku_name ?? h.product_id,
      sub: (h) => (h.l2_category_name ? { text: h.l2_category_name } : undefined),
    },
    { key: 'city', header: 'City', align: 'left', render: (h) => titleCase(h.city_name) ?? h.city_name },
    { key: 'discount', header: 'Discount', render: (h) => pctOf(h.discount_pct) },
    { key: 'asp', header: 'Selling price', render: (h) => inrExact(h.asp) },
    { key: 'mrp', header: 'MRP', render: (h) => inrExact(h.avg_mrp) },
    { key: 'given', header: 'Given away', render: (h) => inr(h.discount_value) },
    { key: 'units', header: 'Units', render: (h) => units(h.units) },
    { key: 'osa', header: 'Wt OSA', render: (h) => pctOf(h.wt_osa) },
  ];

  const spreadColumns: MetricColumn<DiscountSpreadRow>[] = [
    { key: 'sku', header: 'SKU', align: 'left', width: '30%', render: (s) => s.sku_name ?? s.product_id },
    { key: 'cities', header: 'Cities', render: (s) => plain(s.cities) },
    {
      key: 'min',
      header: 'Shallowest',
      render: (s) => pctOf(s.min_discount_pct),
      sub: (s) => (s.shallowest_city ? { text: titleCase(s.shallowest_city) ?? s.shallowest_city } : undefined),
    },
    {
      key: 'max',
      header: 'Deepest',
      render: (s) => pctOf(s.max_discount_pct),
      sub: (s) => (s.deepest_city ? { text: titleCase(s.deepest_city) ?? s.deepest_city } : undefined),
    },
    {
      key: 'spread',
      header: 'Gap',
      render: (s) => (s.spread_pct != null ? `${s.spread_pct.toFixed(1)} pts` : '—'),
    },
    { key: 'units', header: 'Units', render: (s) => units(s.units) },
    { key: 'sales', header: 'Sales', render: (s) => inr(s.sales) },
  ];

  return (
    <>
      {query.isError && (
        <DataSourceBanner kind="error" detail={query.error.message} />
      )}

      <KpiStrip items={kpis} loading={loading} />

      <Panel
        title="Price over the window"
        actions={
          <SegmentedControl
            label="Trend metric"
            value={chartMetric}
            onChange={setChartMetric}
            options={[
              { value: 'discount', label: 'Discount' },
              { value: 'prices', label: 'MRP vs selling' },
            ]}
          />
        }
        footnote={data?.response_meta.basis_note}
      >
        {chart.labels.length === 0 ? (
          <EmptyState>No daily prices for this window.</EmptyState>
        ) : (
          <LineChart labels={chart.labels} series={chart.series} height={240} />
        )}
      </Panel>

      <Panel
        title="Where the discount is"
        caption="Sorted deepest first — this page is read to find where price is being given away, not to rank by size."
        actions={
          <SegmentedControl
            label="Breakdown grain"
            value={grain}
            onChange={setGrain}
            options={[
              { value: 'sku', label: 'SKU' },
              { value: 'city', label: 'City' },
              { value: 'category', label: 'Category' },
            ]}
          />
        }
        dense
      >
        <MetricTable
          searchable
          caption={`Discount by ${GRAIN_LABEL[grain]}`}
          columns={columns}
          rows={data?.tables[grain].rows ?? []}
          rowKey={(r) => r.id}
          loading={loading}
          onRowClick={(r) => {
            ctx.setScope({ ...ctx.scope, [grain === 'sku' ? 'sku' : grain]: r.value ?? r.label });
          }}
          empty={`No ${GRAIN_LABEL[grain].toLowerCase()} rows for this scope.`}
        />
      </Panel>

      <Panel
        title="Deepest cells"
        caption="SKU × city — the grain a pricing decision is actually made at."
        footnote={data?.response_meta.limitations}
        dense
      >
        <MetricTable
          searchable
          caption="Deepest SKU-city discounts"
          columns={hotspotColumns}
          rows={data?.hotspots ?? []}
          rowKey={(h) => `${h.product_id}-${h.city_name}`}
          loading={loading}
          empty="No SKU × city cell cleared the volume floor in this window."
        />
      </Panel>

      <Panel
        title="Same SKU, different cities"
        caption="How inconsistent the pricing is. A national average hides this entirely."
        dense
      >
        <MetricTable
          searchable
          caption="Discount spread by SKU"
          columns={spreadColumns}
          rows={data?.spread ?? []}
          rowKey={(s) => s.product_id}
          loading={loading}
          empty="No SKU sold in two or more cities above the volume floor."
        />
      </Panel>
    </>
  );
}
